import React, { memo, useState } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { ExperimentNodeData } from '../../types';
import { getComponentDef } from '../../engine/componentLibrary';
import { useSimulatorStore } from '../../store/simulatorStore';

const ExperimentNode = memo(({ data, id, selected }: NodeProps<ExperimentNodeData>) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const { removeNode } = useSimulatorStore();
  const def = getComponentDef(data.componentType);

  const color = def?.color ?? '#6366f1';
  const bgColor = def?.bgColor ?? '#ede9fe';

  return (
    <div
      style={{
        border: `2px solid ${selected ? color : '#d1d5db'}`,
        borderRadius: 12,
        background: bgColor,
        minWidth: 110,
        fontFamily: 'system-ui, sans-serif',
        boxShadow: selected
          ? `0 0 0 3px ${color}44, 0 4px 12px rgba(0,0,0,0.15)`
          : '0 2px 8px rgba(0,0,0,0.08)',
        transition: 'all 0.15s',
        position: 'relative',
      }}
    >
      {/* Handles for inputs */}
      {data.inputs?.map((input, i) => (
        <Handle
          key={input.id}
          type="target"
          position={Position.Left}
          id={input.id}
          style={{
            top: data.inputs.length === 1 ? '50%' : `${30 + i * 24}px`,
            background: color,
            width: 10,
            height: 10,
            border: '2px solid white',
          }}
        />
      ))}

      {/* Node body */}
      <div style={{ padding: '10px 12px' }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 4,
          }}
        >
          <span style={{ fontSize: 20 }}>{data.icon}</span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: '#1e293b',
              lineHeight: 1.2,
              maxWidth: 80,
            }}
          >
            {data.label}
          </span>

          {/* Info button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowTooltip((v) => !v);
            }}
            style={{
              marginLeft: 'auto',
              background: 'none',
              border: `1px solid ${color}`,
              borderRadius: '50%',
              width: 16,
              height: 16,
              fontSize: 9,
              cursor: 'pointer',
              color: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            ?
          </button>
        </div>

        {/* Category badge */}
        <div
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: color,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {data.category}
        </div>
      </div>

      {/* Tooltip overlay */}
      {showTooltip && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginTop: 8,
            background: '#1e293b',
            color: 'white',
            borderRadius: 8,
            padding: '8px 12px',
            fontSize: 11,
            lineHeight: 1.5,
            width: 200,
            zIndex: 1000,
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            pointerEvents: 'none',
          }}
        >
          {data.tooltip}
          <div
            style={{
              position: 'absolute',
              top: -6,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '6px solid transparent',
              borderRight: '6px solid transparent',
              borderBottom: '6px solid #1e293b',
            }}
          />
        </div>
      )}

      {/* Delete button (visible on selection) */}
      {selected && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            removeNode(id);
          }}
          style={{
            position: 'absolute',
            top: -8,
            right: -8,
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: 18,
            height: 18,
            fontSize: 11,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            zIndex: 10,
          }}
          title="Remove component"
        >
          ×
        </button>
      )}

      {/* Handles for outputs */}
      {data.outputs?.map((output, i) => (
        <Handle
          key={output.id}
          type="source"
          position={Position.Right}
          id={output.id}
          style={{
            top: data.outputs.length === 1 ? '50%' : `${30 + i * 24}px`,
            background: color,
            width: 10,
            height: 10,
            border: '2px solid white',
          }}
        />
      ))}
    </div>
  );
});

ExperimentNode.displayName = 'ExperimentNode';
export default ExperimentNode;
